import { useState } from 'react';
import { useLocation } from 'react-router-dom';

import qs from 'qs';
import styled from 'styled-components';

import { ActionText } from '@components/action-text';
import { createForm } from '@components/form';
import { LabelTextGroup } from '@components/label-text-group';
import { RelativeSpinner } from '@components/loading-spinner';
import { PasswordStrengthBar } from '@components/password-strength-bar';
import { usePasswordValidations } from '@components/password-strength-bar/password-strength-bar';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useScopedTranslate } from '@locale/translate';
import { pxToRem } from '@utils/converters';

import { useCanSetPassword, useSignUpUser } from 'api/users/hooks';

import { Title } from './common';
import { InvalidLink } from './reset-password';

export type SignUpData = {
  fullName: string;
  phone: string;
  password: string;
};

const Form = createForm<SignUpData>();

export const SignupForm = ({
  uuid,
  passwordUuid,
  email,
}: {
  uuid: string;
  passwordUuid: string;
  email: string;
}) => {
  const { t } = useScopedTranslate(locale);
  const signUpMutation = useSignUpUser();
  const [password, setPassword] = useState('');

  const handleSave = (data: SignUpData) => {
    signUpMutation.mutate({
      uuid,
      passwordUuid,
      fullName: data.fullName,
      phone: data.phone,
      password: data.password,
    });
  };

  const { isAllValid, ValidationMessages } = usePasswordValidations(password);

  return (
    <Stack space={6}>
      <div>
        <Title>{t('welcome')}</Title>
        <Title>{t('signUp')}</Title>
      </div>
      <LabelTextGroup label={t('emailAddress')} text={email} />
      <Form onSubmit={handleSave}>
        <Form.TextField
          id="fullName"
          placeholder={t('enterFullName')}
          label={t('core.fullName')}
          validators={['required']}
        />
        <Form.PhoneField id="phone" label={t('core.phone')} />
        <Stack space={2}>
          <Form.PasswordField
            id="password"
            label={t('password')}
            validators={['required']}
            onChange={(value) => setPassword(value)}
          />
          <PasswordStrengthBar password={password} />
          <ValidationMessages />
        </Stack>

        <Text
          font="regular14"
          color="textSecondary"
          text={
            <>
              {t('bySigningUpIAgree')}
              <ActionText.ExternalLink
                font="regular14"
                href="https://50skills.com/terms-of-use"
                hoverColor="textPrimary"
              >
                {t('termsOfService')}
              </ActionText.ExternalLink>
              {t('andAcknowledge')}
              <ActionText.ExternalLink
                font="regular14"
                href="https://50skills.com/privacy-policy"
                hoverColor="textPrimary"
              >
                {t('privacyPolicy')}
              </ActionText.ExternalLink>
            </>
          }
        />

        <Form.Submit variant="primary-blue" isDisabled={!isAllValid} text={t('continue')} />
      </Form>
    </Stack>
  );
};

export const SignupPage = () => {
  const [isInitialized, setIsInitialized] = useState(false);
  const location = useLocation();
  const parsed = qs.parse(location.search, { ignoreQueryPrefix: true });
  const uuid = parsed['uuid'] as string;
  const passwordUuid = parsed['password_uuid'] as string;
  const email = parsed['email'] as string;

  const canSetPasswordQuery = useCanSetPassword(
    { uuid, passwordUuid },
    {
      onSettled: () => {
        setIsInitialized(true);
      },
    }
  );
  const isSuccess = isInitialized && canSetPasswordQuery.isSuccess;

  return (
    <>
      {!isInitialized && <RelativeSpinner />}
      {isSuccess ? (
        <SignUpContainer>
          <SignupForm uuid={uuid} passwordUuid={passwordUuid} email={email} />
        </SignUpContainer>
      ) : (
        <InvalidLink />
      )}
    </>
  );
};

const SignUpContainer = styled.div({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  height: pxToRem(800),
});

const locale = {
  en: {
    welcome: 'Welcome to Journeys',
    signUp: 'Sign up to join your team',
    emailAddress: 'Email address',
    enterFullName: 'Enter full name',
    password: 'Password',
    bySigningUpIAgree: 'By signing up, I accept the 50skills ',
    andAcknowledge: ' and acknowledge the ',
    termsOfService: 'Terms of Service',
    privacyPolicy: 'Privacy Policy',
    continue: 'Continue',
  },
};
